import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/* HOVE buy box: finish swatches, one/two pack, and add to cart through Horizon's cart events
   so the cart drawer and bubble update like the native product form. */
class HvBuy extends HTMLElement {
  connectedCallback() {
    if (this.__ready) return;
    this.__ready = true;
    const raw = this.querySelector('[data-hv-data]');
    if (!raw) return;
    this.data = JSON.parse(raw.textContent);
    this.pack = 1;
    this.finishBtns = [...this.querySelectorAll('[data-hv-finishes] .hv-fin')];
    const checked = this.finishBtns.find((b) => b.getAttribute('aria-checked') === 'true') || this.finishBtns[0];
    this.finish = checked ? checked.dataset.value : null;
    this.second = this.finish;

    this.finishBtns.forEach((b) => b.addEventListener('click', () => this.setFinish(b.dataset.value)));
    this.radioKeys(this.querySelector('[data-hv-finishes]'), '.hv-fin', (b) => this.setFinish(b.dataset.value));
    const packs = this.querySelector('[data-hv-packs]');
    if (packs) {
      packs.querySelectorAll('.hv-pack').forEach((b) => b.addEventListener('click', () => this.setPack(+b.dataset.n)));
      this.radioKeys(packs, '.hv-pack', (b) => this.setPack(+b.dataset.n));
    }
    this.buildSecond();
    this.addon = this.querySelector('[data-hv-addon]');
    this.addonOn = false;
    this.addon?.addEventListener('click', () => { this.addonOn = !this.addonOn; this.addon.setAttribute('aria-checked', String(this.addonOn)); this.update(); });
    this.form = this.querySelector('[data-hv-form]');
    this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.add(); });

    this.sticky = this.querySelector('[data-hv-sticky]');
    if (this.sticky) {
      this.sticky.querySelector('a')?.addEventListener('click', (e) => { e.preventDefault(); window.__hvScrollTo ? window.__hvScrollTo(this) : this.scrollIntoView({ behavior: 'smooth' }); });
      // at most once per frame, and never while a menu or drawer covers the page
      this.onScroll = () => { if (this._stickyRaf) return; this._stickyRaf = requestAnimationFrame(() => { this._stickyRaf = 0; if (!(window.__hvPaused && window.__hvPaused())) this.updateSticky(); }); };
      document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      addEventListener('resize', this.onScroll);
      this.updateSticky();
    }
    if (this.finish) this.setFinish(this.finish, true);
    this.update();
  }

  disconnectedCallback() {
    if (this.onScroll) {
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      removeEventListener('resize', this.onScroll);
    }
  }

  radioKeys(group, sel, cb) {
    if (!group) return;
    group.addEventListener('keydown', (e) => {
      const btns = [...group.querySelectorAll(sel)];
      let i = btns.indexOf(document.activeElement);
      if (i < 0) return;
      if (['ArrowDown', 'ArrowRight'].includes(e.key)) i = (i + 1) % btns.length;
      else if (['ArrowUp', 'ArrowLeft'].includes(e.key)) i = (i + btns.length - 1) % btns.length;
      else return;
      e.preventDefault();
      btns[i].focus();
      cb(btns[i]);
    });
  }

  /* ---------- money ---------- */
  money(cents) {
    const fmt = this.data.moneyFormat || '${{amount}}';
    const m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const kind = m ? m[1] : 'amount';
    const n = cents / 100;
    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const whole = Math.floor(n), frac = Math.round((n - whole) * 100).toString().padStart(2, '0');
    const trim = frac === '00';
    let out;
    switch (kind) {
      case 'amount_no_decimals': out = group(String(Math.round(n)), ','); break;
      case 'amount_with_comma_separator': out = group(String(whole), '.') + (trim ? '' : ',' + frac); break;
      case 'amount_no_decimals_with_comma_separator': out = group(String(Math.round(n)), '.'); break;
      case 'amount_with_apostrophe_separator': out = group(String(whole), "'") + (trim ? '' : '.' + frac); break;
      default: out = group(String(whole), ',') + (trim ? '' : '.' + frac);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]*>/g, '');
  }

  /* ---------- variants ---------- */
  variantFor(finish) {
    const { variants, mode, colorIndex } = this.data;
    if (mode !== 'variant') return this.baseVariant();
    const base = this.baseVariant();
    return variants.find((v) => v.options[colorIndex] === finish && v.options.every((o, i) => i === colorIndex || o === base.options[i]))
      || variants.find((v) => v.options[colorIndex] === finish) || null;
  }
  baseVariant() {
    const id = +(this.querySelector('[data-hv-id]')?.value || 0);
    return this.data.variants.find((v) => v.id === id) || this.data.variants[0];
  }
  finishName(value) { return value; }

  setFinish(value, silent) {
    this.finish = value;
    this.finishBtns.forEach((b) => b.setAttribute('aria-checked', b.dataset.value === value ? 'true' : 'false'));
    const btn = this.finishBtns.find((b) => b.dataset.value === value);
    if (btn && btn.dataset.accent && window.__hvSetAccent) window.__hvSetAccent(btn.dataset.accent);
    else if (btn) window.__hvAccent = btn.dataset.accent;
    const idInput = this.querySelector('[data-hv-id]');
    if (this.data.mode === 'variant') {
      const v = this.variantFor(value);
      if (v && idInput) idInput.value = v.id;
      if (v?.image) { const img = this.querySelector('.hv-buy__img'); if (img) { img.src = v.image; img.removeAttribute('srcset'); } }
      if (!silent && v) {
        const url = new URL(location.href); url.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', url.toString());
      }
    }
    const prop = this.querySelector('[data-hv-prop]');
    if (prop) prop.value = value;
    if (this.pack === 1 || !this.secondTouched) this.setSecond(value, true);
    this.update();
  }

  // tell the 3D viewers whether to bring in a second unit, and in which finish
  publishTwin() {
    const b = this.finishBtns.find((x) => x.dataset.value === this.second) || this.finishBtns.find((x) => x.dataset.value === this.finish);
    window.__hvTwin = { on: this.pack === 2, accent: (b && b.dataset.accent) || window.__hvAccent };
    document.dispatchEvent(new CustomEvent('hv:twin'));
  }

  setPack(n) {
    this.pack = n;
    this.querySelectorAll('.hv-pack').forEach((b) => b.setAttribute('aria-checked', +b.dataset.n === n ? 'true' : 'false'));
    const sec = this.querySelector('[data-hv-second]');
    if (sec) sec.hidden = n !== 2;
    this.publishTwin();
    this.update();
  }

  buildSecond() {
    const wrap = this.querySelector('[data-hv-second-sw]');
    if (!wrap) return;
    wrap.innerHTML = '';
    this.finishBtns.forEach((b) => {
      const el = document.createElement('button');
      el.type = 'button'; el.className = 'hv-msw'; el.setAttribute('role', 'radio');
      el.dataset.s = b.dataset.value;
      el.setAttribute('aria-checked', b.dataset.value === this.second ? 'true' : 'false');
      const i = document.createElement('i'); i.style.background = b.dataset.dot || b.dataset.accent || '#ddd';
      el.append(i, document.createTextNode(b.dataset.value));
      el.addEventListener('click', () => { this.secondTouched = true; this.setSecond(el.dataset.s); });
      wrap.appendChild(el);
    });
    this.radioKeys(wrap, '.hv-msw', (el) => { this.secondTouched = true; this.setSecond(el.dataset.s); });
  }
  setSecond(value, quiet) {
    this.second = value;
    this.querySelectorAll('[data-hv-second-sw] .hv-msw').forEach((b) => b.setAttribute('aria-checked', b.dataset.s === value ? 'true' : 'false'));
    this.publishTwin();
    if (!quiet) this.update();
  }

  /* ---------- UI ---------- */
  update() {
    const d = this.data, v = this.data.mode === 'variant' ? this.variantFor(this.finish) : this.baseVariant();
    const second = this.data.mode === 'variant' ? this.variantFor(this.second) : v;
    const n = this.pack;
    const price = v ? v.price : 0, compare = v && v.compare > v.price ? v.compare : 0;
    const p2 = second ? second.price : price, c2 = second && second.compare > second.price ? second.compare : p2;
    const addonPrice = this.addonOn && this.addon ? Number(this.addon.dataset.price) : 0;
    const total = (n === 1 ? price : Math.max(0, price + p2 - d.pairDiscount)) + addonPrice;
    const was = (n === 1 ? compare : (compare || price) + c2) + addonPrice;
    const set = (sel, txt) => this.querySelectorAll(sel).forEach((el) => (el.textContent = txt));
    set('[data-hv-price]', this.money(price));
    set('[data-hv-sticky-price]', this.money(price));
    this.querySelectorAll('[data-hv-compare]').forEach((el) => { el.hidden = !compare; if (compare) el.textContent = this.money(compare); });
    set('[data-hv-one]', this.money(price));
    set('[data-hv-two]', this.money(Math.max(0, price * 2 - d.pairDiscount)));
    set('[data-hv-two-was]', this.money((compare || price) * 2));
    set('[data-hv-sum-price]', this.money(total));
    this.querySelectorAll('[data-hv-sum-was]').forEach((el) => { el.hidden = !(was > total); el.textContent = this.money(was); });

    let name = d.shortName;
    if (d.mode !== 'none' && this.finish) {
      if (n === 1) name += ' ' + d.inWord + ' ' + this.finish;
      else if (this.second === this.finish) name = '2 × ' + d.shortName + ' ' + d.inWord + ' ' + this.finish;
      else name += ' ' + d.inWord + ' ' + this.finish + ' + ' + this.second;
    } else if (n === 2) name = '2 × ' + d.shortName;
    if (this.addonOn && this.addon) name += ' ' + (d.addonJoin || '+') + ' ' + this.addon.dataset.title;
    set('[data-hv-sum-name]', name);

    const ok = v && v.available && (n === 1 || (second && second.available));
    const btn = this.querySelector('[data-hv-add]');
    if (btn) {
      btn.disabled = !ok;
      btn.textContent = !v ? d.labels.unavailable : !ok ? d.labels.soldout : n === 1 ? d.labels.add : d.labels.addTwo;
    }
    this.finishBtns.forEach((b) => { const fv = this.data.mode === 'variant' ? this.variantFor(b.dataset.value) : v; b.dataset.available = fv && fv.available ? 'true' : 'false'; });
    // express checkout buys what the form holds: one unit, or two of the same finish
    const qty = this.querySelector('[data-hv-qty]');
    if (qty) qty.value = n === 2 && this.second === this.finish ? 2 : 1;
    const pb = this.querySelector('.shopify-payment-button');
    if (pb) pb.style.display = n === 2 && this.second !== this.finish ? 'none' : '';
  }

  updateSticky() {
    const r = this.getBoundingClientRect();
    const st = window.__hvScrollTop ? window.__hvScrollTop() : scrollY;
    const footer = document.querySelector('footer')?.getBoundingClientRect();
    const inView = r.top < innerHeight * 0.85 && r.bottom > innerHeight * 0.15;
    const show = st > innerHeight * 0.8 && !inView && !(footer && footer.top < innerHeight);
    this.sticky.classList.toggle('show', show);
    this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    this.sticky.querySelector('a')?.setAttribute('tabindex', show ? '0' : '-1');
  }

  /* ---------- cart ---------- */
  lines() {
    const d = this.data;
    const prop = d.mode === 'property' ? this.querySelector('[data-hv-prop]') : null;
    const propName = prop ? prop.name.replace(/^properties\[|\]$/g, '') : null;
    const line = (id, finish, qty) => { const l = { id, quantity: qty }; if (propName) l.properties = { [propName]: finish }; return l; };
    const first = d.mode === 'variant' ? this.variantFor(this.finish) : this.baseVariant();
    const extra = this.addonOn && this.addon ? [{ id: Number(this.addon.dataset.id), quantity: 1 }] : [];
    if (this.pack === 1) return [line(first.id, this.finish, 1), ...extra];
    const second = d.mode === 'variant' ? this.variantFor(this.second) : first;
    if (second.id === first.id && this.second === this.finish) return [line(first.id, this.finish, 2), ...extra];
    return [line(first.id, this.finish, 1), line(second.id, this.second, 1), ...extra];
  }

  async add() {
    const btn = this.querySelector('[data-hv-add]');
    const err = this.querySelector('[data-hv-error]');
    if (!btn || btn.disabled) return;
    err.textContent = '';
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    const items = this.lines();
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    }));
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    try {
      const res = await fetch((window.Theme?.routes?.cart_add_url) || root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: location.pathname }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw Object.assign(new Error(json.description || json.message || 'Could not add to bag'), { code: 'INVALID' });
      const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: this.dataset.sectionId,
          itemCount: items.reduce((a, l) => a + l.quantity, 0),
          productId: String(this.data.productId),
          sections: json.sections,
          didError: false,
        },
      });
      if (!this.data.openCart) return;
      // Horizon opens its drawer on add when 'auto-open' is set; otherwise open it here, or go to the cart page
      const cd = document.querySelector('cart-drawer-component');
      const td = cd?.closest('theme-drawer');
      if (td) { if (!cd.hasAttribute('auto-open')) setTimeout(() => !td.isOpen && td.open?.(), 150); }
      else location.href = (window.Theme?.routes?.cart_url) || root + 'cart';
    } catch (e) {
      err.textContent = e.message;
      deferred.reject(e);
      try { this.dispatchEvent(new CartErrorEvent({ error: e.message, code: e.code || 'SERVICE_UNAVAILABLE' })); } catch (_) {}
    } finally {
      btn.removeAttribute('aria-busy');
      this.update();
    }
  }
}
if (!customElements.get('hv-buy')) customElements.define('hv-buy', HvBuy);
